import React from "react"
import PropTypes from "prop-types"
import tw, { styled } from "twin.macro"
import { GatsbyImage, getImage } from "gatsby-plugin-image"
import parse from "html-react-parser"
import MovingElement from "@elements/MovingElement"

const Section = styled.section`
  ${tw`z-10 grid grid-cols-none grid-rows-1 md:grid-cols-2 py-c40 2xl:py-c96`}
`
const TextColumn = styled.div`
  ${tw`relative flex-col justify-center w-full row-span-1 md:min-h-20vw min-h-50vw md:row-span-2`}
`
const HeadlineWrap = styled.div`
  ${tw`relative z-10 text-4xl font-black 2xl:text-5xl font-ubuntu`}
`
const Description = styled.div`
  ${tw`relative z-10 font-normal`}
  p + p, p + ul {
    ${tw`mt-6`}
  }
  &.simple-description {
    & span.line {
      ${tw`absolute w-px -left-3 top-c68 md:top-c60 2xl:top-c68 bottom-8 bg-lightGrey`}
    }
  }
  & ul {
    li {
      ${tw`relative pl-10 mb-4`}

      &::before {
        content: "|->";
        position: absolute;
        left: 0;
        ${tw`font-firacode`}
      }
    }
  }
`
const ImageColumn = styled.div`
  ${tw`relative`}
`

const WpAcfTextImageModuleBlock = ({ moduleData }) => {
  const blockData = moduleData.acfTextImageBlock
  const sectionAttributes = moduleData.attributes
  const sectionId = sectionAttributes.anchor || ""
  const image = getImage(blockData.image?.localFile)
  const secondImage = getImage(blockData.secondImage?.localFile)

  return (
    <Section
      style={{ backgroundColor: blockData.backgroundColor }}
      id={sectionId}
    >
      <TextColumn
        className={`${
          blockData.contentColumnPosition === "right"
            ? "order-1 px-offset sm:px-12 md:pl-0 md:pr-16 xl:pr-24"
            : "px-offset sm:px-12 lg:px-16 xl:px-24"
        } ${
          blockData.topSpacing === "no_gap"
            ? "pt-0 2xl:pt-0"
            : blockData.topSpacing === "negative"
            ? "pt-0 2xl:pt-0 md:-mt-c96 2xl:-mt-c120"
            : "pt-c120 md:pt-c96 2xl:pt-c120"
        } ${
          blockData.bottomSpacing === "no_gap"
            ? "pb-c40 2xl:pb-0"
            : blockData.bottomSpacing === "negative"
            ? "pb-c40 2xl:pb-0 md:-mb-c96 2xl:-mb-c120"
            : "pb-c200 md:pb-c120 2xl:pb-c200"
        } ${blockData.contentBlocks?.length > 0 ? "flex" : "hidden md:flex"}`}
      >
        {blockData.contentBlocks?.map((textBlock, i) => {
          const HeadingTag = textBlock.headingType || "h2"
          return (
            <div key={`cc-${i}`}>
              {textBlock.headline !== null && (
                <HeadlineWrap className={i > 0 ? "mt-12" : ""}>
                  <HeadingTag
                    style={{
                      color: blockData.headlineColor || "#000",
                    }}
                    className="relative font-bold headline-section font-ubuntu"
                  >
                    <span
                      className="relative"
                      data-start-symbol={textBlock.startSymbol}
                      data-end-symbol={textBlock.endSymbol}
                    >
                      {parse(textBlock.headline)}
                    </span>
                  </HeadingTag>
                  {textBlock.subline !== null && (
                    <p
                      style={{
                        color: blockData.sublineColor,
                      }}
                      className={`subline-section ${
                        textBlock.sublineSmall ? "subline-section--small" : ""
                      }`}
                    >
                      {parse(textBlock.subline)}
                    </p>
                  )}
                </HeadlineWrap>
              )}
              {textBlock.description !== null && (
                <Description
                  style={{
                    color: blockData.descriptionColor,
                  }}
                  className={`copy ${
                    !textBlock.description.includes("<ul>")
                      ? "simple-description"
                      : ""
                  } ${
                    blockData.contentColumnPosition === "right"
                      ? "lg:pr-12 lg:w-4/5"
                      : "lg:pr-12 2xl:pr-28"
                  }`}
                >
                  {parse(textBlock.description)}
                  {!textBlock.description.includes("<ul>") && (
                    <>
                      <span
                        style={{
                          backgroundColor:
                            blockData.descriptionColor === "#ffffff"
                              ? "#ffffff"
                              : "#8888A5",
                        }}
                        className="line"
                      ></span>
                      <span
                        style={{
                          color:
                            blockData.descriptionColor === "#ffffff"
                              ? "#ffffff"
                              : "#8888A5",
                        }}
                        className="absolute line-top top-c40 md:top-c32 2xl:top-c40 -left-5 2xl:-left-6"
                      >{`/*`}</span>
                      <span
                        style={{
                          color:
                            blockData.descriptionColor === "#ffffff"
                              ? "#ffffff"
                              : "#8888A5",
                        }}
                        className="absolute bottom-0 line-bottom -left-5 2xl:-left-6"
                      >{`*/`}</span>
                    </>
                  )}
                </Description>
              )}
            </div>
          )
        })}
      </TextColumn>

      <ImageColumn
        className={`${
          blockData.verticalAlign === "top"
            ? "items-start"
            : blockData.verticalAlign === "center"
            ? "items-center"
            : "items-end"
        } ${
          blockData.horizontalAlign === "left"
            ? "justify-start"
            : blockData.horizontalAlign === "center"
            ? "justify-center"
            : "justify-end"
        } ${
          blockData.contentColumnPosition === "right"
            ? "px-offset md:px-offsetMd md:mr-16 lg:pl-0"
            : "px-offset md:px-0 md:pr-offset lg:pr-offsetMd"
        } ${
          blockData.topSpacing === "no_gap"
            ? "pt-0"
            : blockData.topSpacing === "negative"
            ? "pt-0 2xl:pt-0 md:-mt-c48 2xl:-mt-c60"
            : "pt-c60 md:pt-c48 2xl:pt-c60"
        } ${
          blockData.bottomSpacing === "no_gap"
            ? "pb-0"
            : blockData.bottomSpacing === "negative"
            ? "pb-0 2xl:pb-0 md:-mb-c48 2xl:-mb-c60"
            : "pb-c60 md:pb-c48 2xl:pb-c60"
        } ${
          blockData.hideOnMobile ||
          blockData.image === null ||
          blockData.image === undefined
            ? "hidden md:flex"
            : "flex"
        }`}
      >
        {image !== null && image !== undefined && (
          <GatsbyImage
            objectFit="contain"
            className={`rounded-[30px] ${
              secondImage !== null && secondImage !== undefined
                ? "mb-20 w-52% md:w-1/2"
                : ""
            }`}
            image={image}
            alt={blockData.image?.altText}
          />
        )}
        {secondImage !== null && secondImage !== undefined && (
          <>
            <MovingElement
              speed={0.05}
              direction={{
                negative: "+",
                axis: "y",
              }}
              content={false}
              className="absolute right-0 w-45%  mt-auto ml-auto transition-all duration-500 ease-in-out md:w-6/12 top-c60 md:top-c48 2xl:top-c60 ml-c20 h-1/2 lg:pr-offsetMd"
            >
              <GatsbyImage
                objectFit="contain"
                className="w-full rounded-[30px] -translate-x-c30 sm:-translate-x-c50 md:-translate-x-c50 "
                image={secondImage}
                alt={blockData.secondImage?.altText}
              />
            </MovingElement>
            <div className="absolute left-0 right-0 z-10 flex flex-col items-center mx-auto floating-symbol bottom-60">
              <span className="block font-normal text-center font-firacode text-symbolSm md:text-symbolMd 2xl:text-symbolXl text-customGrey">
                {parse(
                  `<&zwnj;Lassen Sie uns><br />|-> miteinander <-|<br /><&zwnj;/sprechen>`
                )}
              </span>
            </div>
          </>
        )}
      </ImageColumn>
    </Section>
  )
}

WpAcfTextImageModuleBlock.propTypes = {
  moduleData: PropTypes.object,
}

WpAcfTextImageModuleBlock.defaultProps = {
  moduleData: ``,
}

export default WpAcfTextImageModuleBlock
